/**
 * Web Login Page
 * 
 * Clean composition layer using the Template + Wrapper pattern.
 * 
 * Architecture:
 * - Uses AuthLayoutTemplate for the two-column layout
 * - Uses LoginTemplate for the login form
 * - Handles authentication logic via useAuthDialog hook
 * - Follows the same pattern as Register.tsx
 * 
 * Benefits:
 * - Minimal code (wrapper delegates to templates)
 * - Single source of truth for auth logic
 * - Easy to test and maintain
 * - Reusable templates
 */

import React, { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayoutTemplate, LoginTemplate, showToast } from '@ideoz/ui';
import { useAuthDialog } from '../hooks/useAuthDialog';
import { useAppSelector } from '@ideoz/core/store';

export default function Login(): JSX.Element {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const authDialog = useAuthDialog();
    const isLoggedIn = useAppSelector((state) => state.app.isLoggedIn);

    // Redirect if already logged in
    React.useEffect(() => {
        if (isLoggedIn) {
            const pairingKey = searchParams.get('pairing_key');
            if (pairingKey) {
                navigate(`/redirect?pairing_key=${pairingKey}`);
            } else {
                navigate('/');
            }
        }
    }, [isLoggedIn, searchParams, navigate]);

    // Handle login (email/password)
    const handleLogin = useCallback(
        async (email: string, password: string) => {
            try {
                // Get URL parameters for plugin redirect
                const pairingKey = searchParams.get('pairing_key');
                const figmaSource = searchParams.get('figma_source');


                // Use authDialog's login handler
                await authDialog.handleLogin(email, password, { pairingKey, figmaSource });

                // Success! User will be redirected based on onboarding status
                showToast({
                    message: 'Login successful!',
                    severity: 'success',
                    type: 'success',
                });
            } catch (error: any) {
                // Error is thrown from useAuthDialog, re-throw to let LoginTemplate handle it
                throw error;
            }
        },
        [authDialog, searchParams]
    );

    // Handle Google OAuth
    const handleGoogleAuth = useCallback(async () => {
        try {
            // Get URL parameters for plugin redirect
            const pairingKey = searchParams.get('pairing_key');
            const figmaSource = searchParams.get('figma_source');
            console.log('Login handleGoogleAuth - pairingKey:', pairingKey, 'figmaSource:', figmaSource);
            console.log('Login handleGoogleAuth - current URL:', window.location.href);

            await authDialog.handleGoogleAuth({ pairingKey, figmaSource });
            // Success will be handled by the Google auth callback
        } catch (error: any) {
            // Error is thrown from useAuthDialog, re-throw to let LoginTemplate handle it
            throw error;
        }
    }, [authDialog, searchParams]);

    // Navigate to register page
    const handleNavigateToRegister = useCallback(() => {
        // Preserve URL parameters when switching to register
        const pairingKey = searchParams.get('pairing_key');
        const figmaSource = searchParams.get('figma_source');

        if (pairingKey || figmaSource) {
            const params = new URLSearchParams();
            if (pairingKey) params.set('pairing_key', pairingKey);
            if (figmaSource) params.set('figma_source', figmaSource);
            navigate(`/register?${params.toString()}`);
        } else {
            navigate('/register');
        }
    }, [navigate, searchParams]);

    // Navigate to forgot password page
    const handleNavigateToForgotPassword = useCallback(() => {
        navigate('/forgot-password');
    }, [navigate]);

    return (
        <AuthLayoutTemplate isWeb={true}>
            <LoginTemplate
                onLogin={handleLogin}
                onGoogleAuth={handleGoogleAuth}
                onNavigateToRegister={handleNavigateToRegister}
                onNavigateToForgotPassword={handleNavigateToForgotPassword}
                isLoading={authDialog.isLoading}
                isWeb={true}
                initialError={authDialog.error}
            />
        </AuthLayoutTemplate>
    );
}
